import React, { useContext } from "react";
import { UserContext } from "../context/UserContext";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaCrown, FaStar, FaBolt, FaLock } from "react-icons/fa";

const SubscriptionPlans = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const currentPlan = user?.subscription?.tier || "trial";

  // Verifică dacă utilizatorul este VisionRoyale
  const isVisionRoyale = user?.username === "VisionRoyale";

  // Funcție pentru a obține culoarea specifică fiecărui plan
  const getPlanColor = (planTier) => {
    switch (planTier) {
      case "basic":
        return "blue";
      case "pro":
        return "green";
      case "premium":
        return "purple";
      default:
        return "gray";
    }
  };

  // Funcție pentru a obține prefixul culorii Tailwind pentru un plan
  const getColorPrefix = (planTier) => {
    const color = getPlanColor(planTier);
    switch (color) {
      case "blue":
        return "blue";
      case "green":
        return "green";
      case "purple":
        return "purple";
      default:
        return "gray";
    }
  };

  // Funcție pentru a determina textul butonului în funcție de planul curent
  const getButtonText = (planTier) => {
    if (isVisionRoyale) {
      return "Blocked for Test User VisionRoyale";
    }
    if (planTier === currentPlan) {
      return "Current Plan";
    }
    if (currentPlan === "trial") {
      return `Get ${planTier.charAt(0).toUpperCase() + planTier.slice(1)}`;
    }
    return `Upgrade to ${planTier.charAt(0).toUpperCase() + planTier.slice(1)}`;
  };

  // Funcție pentru a obține culoarea textului prețului
  const getPriceColor = (planTier) => {
    const prefix = getColorPrefix(planTier);
    return `text-${prefix}-400`;
  };

  // Funcție pentru a obține culoarea numelui planului
  const getPlanNameColor = (planTier) => {
    const prefix = getColorPrefix(planTier);
    return `text-${prefix}-400`;
  };

  // Funcție pentru a obține culoarea textului secundar (month)
  const getSecondaryTextColor = (planTier) => {
    const prefix = getColorPrefix(planTier);
    return `text-${prefix}-300`;
  };

  // Funcție pentru a obține clasa pentru border în funcție de planul curent
  const getBorderClass = (planTier) => {
    if (isVisionRoyale) {
      return "border-red-500";
    }
    if (planTier === currentPlan) {
      const prefix = getColorPrefix(planTier);
      return `border-${prefix}-500`;
    }
    return "border-gray-700";
  };

  // Funcție pentru a obține culoarea iconiței
  const getIconColor = (planTier) => {
    if (isVisionRoyale) {
      return "text-red-400";
    }
    const prefix = getColorPrefix(planTier);
    return `text-${prefix}-400`;
  };

  // Funcție pentru a obține culoarea badge-ului CURRENT
  const getCurrentBadgeColor = (planTier) => {
    const prefix = getColorPrefix(planTier);
    return `bg-${prefix}-500/20 text-${prefix}-400`;
  };

  const plans = [
    {
      id: 1,
      tier: "basic",
      name: "Basic",
      price: "4.99",
      period: "month",
      features: [
        "Calendar access",
        "Unlimited projects",
        "Today's matches",
        "Basic statistics",
        "Standard support",
      ],
      limitations: [
        "No Daily Value Snapshots",
        "No AI Predictions",
        "No Premium Analysis",
      ],
      popular: false,
    },
    {
      id: 2,
      tier: "pro",
      name: "Pro",
      price: "13.99",
      period: "month",
      features: [
        "Everything in Basic",
        "Daily Value Snapshot",
        "AI Predictions",
        "Advanced statistics",
        "Priority support",
        "Value event analysis",
      ],
      limitations: [
        "No Premium Analysis",
        "Up to 50 daily AI-analyzed matches",
      ],
      popular: false,
    },
    {
      id: 3,
      tier: "premium",
      name: "Premium",
      price: "19.99",
      period: "month",
      features: [
        "Everything in Pro",
        "Premium Analysis",
        "VIP support",
        "Advanced Premium AI models",
        "Exclusive insights",
        "Unlimited daily AI-analyzed matches",
      ],
      limitations: [],
      popular: true,
    },
  ];

  const handleUpgrade = async (planId, planTier) => {
    // Blochează upgrade pentru VisionRoyale
    if (isVisionRoyale) {
      alert(
        "This action is disabled for Test User VisionRoyale. Please use a different account to upgrade.",
      );
      return;
    }

    try {
      // Aici ar trebui să fie logica reală de upgrade (API call, etc.)
      console.log(`Upgrading to ${planTier} plan`);
      // Pentru demo, doar afișăm un alert
      if (currentPlan === "trial") {
        alert(`Successfully subscribed to ${planTier.toUpperCase()} plan!`);
      } else {
        alert(`Successfully upgraded to ${planTier.toUpperCase()} plan!`);
      }
      // Navigare sau refresh date
      navigate("/");
    } catch (error) {
      alert(
        `Upgrade failed: ${error.response?.data?.message || error.message}`,
      );
    }
  };

  // Funcție pentru a obține culoarea gradientului butonului
  const getButtonGradient = (planTier, isDisabled) => {
    if (isDisabled && isVisionRoyale) {
      return "bg-red-500/20 text-red-300 cursor-not-allowed";
    }
    if (isDisabled) {
      const prefix = getColorPrefix(planTier);
      return `bg-${prefix}-500/20 text-${prefix}-300 cursor-not-allowed`;
    }
    const prefix = getColorPrefix(planTier);
    switch (prefix) {
      case "blue":
        return `bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white`;
      case "green":
        return `bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white`;
      case "purple":
        return `bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white`;
      default:
        return "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white";
    }
  };

  // Funcție pentru a obține iconița pentru fiecare plan
  const getPlanIcon = (planTier) => {
    const iconColor = getIconColor(planTier);
    if (isVisionRoyale) {
      return <FaLock className="text-red-400 text-2xl" />;
    }
    switch (planTier) {
      case "premium":
        return <FaCrown className={`${iconColor} text-2xl`} />;
      case "pro":
        return <FaStar className={`${iconColor} text-2xl`} />;
      case "basic":
        return <FaBolt className={`${iconColor} text-2xl`} />;
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              Choose Your Plan
            </h1>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Unlock powerful features to maximize your strategies.
            </p>

            {/* Display current plan status */}
            <div className="mt-6 inline-block bg-gray-800/50 px-6 py-2 rounded-full border border-gray-700">
              <span className="text-gray-400">Current Plan: </span>
              <span
                className={`font-bold ${getPlanNameColor(currentPlan)} capitalize`}
              >
                {currentPlan === "trial" ? "Trial (Free)" : currentPlan}
              </span>
            </div>

            {/* Mesaj pentru utilizatorul VisionRoyale */}
            {isVisionRoyale && (
              <div className="mt-6 bg-red-900/30 border border-red-500 rounded-lg p-4 max-w-2xl mx-auto">
                <div className="flex items-center justify-center gap-3">
                  <p className="text-red-400 font-medium">
                    ⚠️ Subscription upgrades are disabled for Test User
                    VisionRoyale.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan) => {
              const isCurrentPlan = plan.tier === currentPlan;
              const disabled = isCurrentPlan || isVisionRoyale;
              const buttonText = getButtonText(plan.tier);
              const borderClass = getBorderClass(plan.tier);
              const priceColor = getPriceColor(plan.tier);
              const planNameColor = getPlanNameColor(plan.tier);
              const secondaryTextColor = getSecondaryTextColor(plan.tier);
              const buttonGradient = getButtonGradient(plan.tier, disabled);
              const iconColor = getIconColor(plan.tier);
              const currentBadgeColor = getCurrentBadgeColor(plan.tier);

              return (
                <div
                  key={plan.id}
                  className={`bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl p-8 border-2 ${
                    plan.popular && !isVisionRoyale
                      ? "border-purple-500 transform scale-105 shadow-2xl shadow-purple-500/20"
                      : borderClass
                  } ${isVisionRoyale ? "opacity-75" : ""} relative transition-all duration-300 hover:shadow-lg`}
                >
                  {plan.popular && !isVisionRoyale && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="bg-gradient-to-r from-purple-400 to-purple-900 text-white px-6 py-1 rounded-full font-bold text-sm">
                        MOST POPULAR
                      </div>
                    </div>
                  )}
                  <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      {getPlanIcon(plan.tier)}
                      <h3 className={`text-2xl font-bold ${planNameColor}`}>
                        {plan.name}
                      </h3>
                      {isCurrentPlan && !isVisionRoyale && (
                        <span
                          className={`${currentBadgeColor} text-xs px-2 py-1 rounded-full`}
                        >
                          CURRENT
                        </span>
                      )}
                    </div>
                    <div className="mb-4">
                      <span className={`text-5xl font-bold ${priceColor}`}>
                        €{plan.price}
                      </span>
                      <span className={`${secondaryTextColor}`}>
                        /{plan.period}
                      </span>
                    </div>
                    <button
                      onClick={() => handleUpgrade(plan.id, plan.tier)}
                      disabled={disabled}
                      className={`w-full py-3 rounded-lg font-bold text-lg transition-all ${buttonGradient}`}
                    >
                      {buttonText}
                    </button>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-4">
                        Features
                      </h4>
                      <ul className="space-y-3">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center">
                            <FaCheck className="text-green-500 mr-3 flex-shrink-0" />
                            <span className="text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {plan.limitations.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-red-400 mb-4">
                          Limitations
                        </h4>
                        <ul className="space-y-3">
                          {plan.limitations.map((limitation, idx) => (
                            <li key={idx} className="flex items-center">
                              <span className="text-red-400 mr-3">✗</span>
                              <span className="text-gray-400">
                                {limitation}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 max-w-3xl mx-auto">
              <h3 className="text-xl font-bold text-white mb-4">
                Current Plan Status
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-900 p-4 rounded-lg">
                  <div className="text-gray-400 text-sm">Current Tier</div>
                  <div
                    className={`text-2xl font-bold ${getPlanNameColor(currentPlan)} capitalize`}
                  >
                    {currentPlan === "trial" ? "Trial" : currentPlan}
                  </div>
                </div>
                <div className="bg-gray-900 p-4 rounded-lg">
                  <div className="text-gray-400 text-sm">Status</div>
                  <div className="text-2xl font-bold text-green-400 capitalize">
                    {user?.subscription?.status || "active"}
                  </div>
                </div>
                <div className="bg-gray-900 p-4 rounded-lg">
                  <div className="text-gray-400 text-sm">
                    {currentPlan === "trial"
                      ? "Trial Days Left"
                      : "Days Remaining"}
                  </div>
                  <div className="text-2xl font-bold text-yellow-400">
                    {currentPlan === "trial"
                      ? "2"
                      : user?.subscription?.days_remaining || 30}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SubscriptionPlans;
